/**
 * Upgrade Matrix — Admin view for managing tier transition rules.
 *
 * Displays a full-width matrix of all tier/type combinations with editable
 * fee cells. Each cell shows {transactionFee}/{prorationRule}/{firstMonthRule}.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import {
  getTiers,
  getUpgradeRules,
  saveUpgradeRule,
  deleteUpgradeRule,
  getRegions,
  getPromotions,
} from '@/services/adminApi';
import type { TierRecord, UpgradeRule, Promotion } from '@/types';

/** A single row/column label in the matrix: tier + plan type. */
interface TierTypeCombo {
  tierId: string;
  type: string;
  label: string;
  rank: number;
  price: number; // Single member price for this tier/type
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Regions loaded dynamically from API

const TYPE_ORDER: Record<string, number> = { basic: 0, premier: 1, boxing: 2 };

const TYPE_LABELS: Record<string, string> = {
  basic: 'Basic',
  premier: 'Premier',
  boxing: 'Boxing',
};

// ---------------------------------------------------------------------------
// DynamoDB helpers
// ---------------------------------------------------------------------------

// Data loading and saving via admin API (no direct DynamoDB access)
async function loadTiersForRegion(region: string): Promise<TierRecord[]> {
  return getTiers(region);
}

async function loadUpgradeRulesForRegion(fromRegion: string): Promise<UpgradeRule[]> {
  return getUpgradeRules(fromRegion);
}

async function saveRule(rule: UpgradeRule): Promise<void> {
  return saveUpgradeRule(rule);
}

async function deleteRule(rule: UpgradeRule): Promise<void> {
  return deleteUpgradeRule(
    rule.fromRegion, rule.fromTierId, rule.fromType,
    rule.toRegion, rule.toTierId, rule.toType
  );
}

async function loadRegions(): Promise<string[]> {
  return getRegions();
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const VALID_VALUE_RE = /^(X|0|\d+(\.\d{1,2})?|%\d{1,3})$/;

function isValidRuleValue(v: string): boolean {
  return VALID_VALUE_RE.test(v.trim());
}

// ---------------------------------------------------------------------------
// Build tier/type combos
// ---------------------------------------------------------------------------

function buildCombos(tiers: TierRecord[]): TierTypeCombo[] {
  const combos: TierTypeCombo[] = [];
  const sorted = [...tiers].sort((a, b) => a.rank - b.rank);

  for (const tier of sorted) {
    const types: string[] = ['basic', 'premier'];
    if (tier.pricing?.boxing) {
      types.push('boxing');
    }
    for (const t of types) {
      const pricing = tier.pricing?.[t as keyof typeof tier.pricing];
      const price = typeof pricing === 'object' && pricing !== null ? (pricing as any).single || 0 : 0;
      combos.push({
        tierId: tier.tierId,
        type: t,
        label: `${tier.displayName} ${TYPE_LABELS[t]}`,
        rank: tier.rank * 10 + TYPE_ORDER[t],
        price,
      });
    }
  }

  return combos.sort((a, b) => a.rank - b.rank);
}

// ---------------------------------------------------------------------------
// Rule lookup key
// ---------------------------------------------------------------------------

function ruleKey(
  fromTierId: string,
  fromType: string,
  toTierId: string,
  toType: string,
): string {
  return `${fromTierId}|${fromType}|${toTierId}|${toType}`;
}

// ---------------------------------------------------------------------------
// Cell component (memoized for performance)
// ---------------------------------------------------------------------------

interface MatrixCellProps {
  rule: UpgradeRule | undefined;
  promo: Promotion | undefined;
  isDiagonal: boolean;
  fromLabel: string;
  toLabel: string;
  fromPrice: number;
  toPrice: number;
  onClick: () => void;
}

const MatrixCell = React.memo(function MatrixCell({
  rule,
  promo,
  isDiagonal,
  fromLabel,
  toLabel,
  fromPrice,
  toPrice,
  onClick,
}: MatrixCellProps) {
  const priceDiff = toPrice - fromPrice;
  const priceLabel = priceDiff > 0 ? `+$${priceDiff.toFixed(2)}` : priceDiff < 0 ? `-$${Math.abs(priceDiff).toFixed(2)}` : '$0';
  if (isDiagonal) {
    return (
      <TableCell
        sx={{
          backgroundColor: '#424242',
          minWidth: 90,
          p: 0.5,
          cursor: 'default',
        }}
      />
    );
  }

  // Color based on price direction: green = upgrade/same, red = downgrade
  const isUpgrade = priceDiff >= 0;
  const baseBg = isUpgrade ? '#e8f5e9' : '#ffebee';
  const hoverBg = isUpgrade ? '#c8e6c9' : '#ffcdd2';
  const blockedBg = isUpgrade ? '#f1f8e9' : '#fce4ec';
  const priceColor = isUpgrade ? '#2e7d32' : '#c62828';

  if (!rule) {
    return (
      <Tooltip title={`${fromLabel} → ${toLabel} (blocked) ${priceLabel}`} arrow>
        <TableCell
          onClick={onClick}
          sx={{
            backgroundColor: blockedBg,
            minWidth: 50,
            p: 0.25,
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': { backgroundColor: hoverBg },
          }}
        >
          <Typography sx={{ color: priceColor, fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>
            {priceLabel}
          </Typography>
        </TableCell>
      </Tooltip>
    );
  }

  const { transactionFee, prorationRule, firstMonthRule } = rule;
  const baseDisplay = `${transactionFee}/${prorationRule}/${firstMonthRule}`;

  // If there's an active promotion, show the override values instead
  const hasPromo = !!promo;
  const effectiveDisplay = hasPromo
    ? `${promo!.transactionFeeOverride}/${promo!.prorationRuleOverride}/${promo!.firstMonthRuleOverride}`
    : baseDisplay;

  // Purple tint for cells with active promotions
  const cellBg = hasPromo ? '#f3e5f5' : baseBg;
  const cellHover = hasPromo ? '#e1bee7' : hoverBg;

  return (
    <Tooltip
      title={
        hasPromo
          ? `${fromLabel} → ${toLabel} (${priceLabel}) — PROMO: ${promo!.description} (base: ${baseDisplay})`
          : `${fromLabel} → ${toLabel} (${priceLabel})`
      }
      arrow
    >
      <TableCell
        onClick={onClick}
        sx={{
          backgroundColor: cellBg,
          minWidth: 50,
          p: 0.25,
          cursor: 'pointer',
          textAlign: 'center',
          '&:hover': { backgroundColor: cellHover },
          ...(hasPromo && { border: '2px solid #9c27b0' }),
        }}
      >
        <Typography sx={{ color: priceColor, fontSize: '0.75rem', fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
          {priceLabel}
        </Typography>
        <Chip
          label={effectiveDisplay}
          size="small"
          variant={hasPromo ? 'filled' : 'outlined'}
          color={hasPromo ? 'secondary' : undefined}
          sx={{
            fontSize: '0.55rem',
            height: 18,
            maxWidth: '100%',
            '& .MuiChip-label': { px: 0.25 },
          }}
        />
      </TableCell>
    </Tooltip>
  );
});

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  open: boolean;
  fromLabel: string;
  toLabel: string;
  fromPrice: number;
  toPrice: number;
  transactionFee: string;
  prorationRule: string;
  firstMonthRule: string;
  isNew: boolean;
  saving: boolean;
  onTransactionFeeChange: (v: string) => void;
  onProrationRuleChange: (v: string) => void;
  onFirstMonthRuleChange: (v: string) => void;
  onSave: () => void;
  onDelete: () => void;
  onCancel: () => void;
}

function EditDialog({
  open,
  fromLabel,
  toLabel,
  fromPrice,
  toPrice,
  transactionFee,
  prorationRule,
  firstMonthRule,
  isNew,
  saving,
  onTransactionFeeChange,
  onProrationRuleChange,
  onFirstMonthRuleChange,
  onSave,
  onDelete,
  onCancel,
}: EditDialogProps) {
  const feeValid = isValidRuleValue(transactionFee);
  const proValid = isValidRuleValue(prorationRule);
  const fmValid = isValidRuleValue(firstMonthRule);
  const allValid = feeValid && proValid && fmValid;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNew ? 'Create Rule' : 'Edit Rule'}: {fromLabel} &rarr; {toLabel}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 4, mb: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">From</Typography>
            <Typography variant="body2" fontWeight={600}>${fromPrice.toFixed(2)}/mo</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">To</Typography>
            <Typography variant="body2" fontWeight={600}>${toPrice.toFixed(2)}/mo</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Difference</Typography>
            <Typography variant="body2" fontWeight={600} color={toPrice > fromPrice ? 'success.main' : 'error.main'}>
              {toPrice >= fromPrice ? '+' : '-'}${Math.abs(toPrice - fromPrice).toFixed(2)}
            </Typography>
          </Box>
        </Box>
        <TextField
          label="Transaction Fee"
          helperText="X=default, 0=free, or dollar amount"
          value={transactionFee}
          onChange={(e) => onTransactionFeeChange(e.target.value)}
          error={transactionFee.length > 0 && !feeValid}
          size="small"
          fullWidth
          autoFocus
        />
        <TextField
          label="Proration Rule"
          helperText="X=standard, 0=free, dollar amount, or %nn=discount"
          value={prorationRule}
          onChange={(e) => onProrationRuleChange(e.target.value)}
          error={prorationRule.length > 0 && !proValid}
          size="small"
          fullWidth
        />
        <TextField
          label="First Month Rule"
          helperText="X=full rate, 0=free, dollar amount, or %nn=discount"
          value={firstMonthRule}
          onChange={(e) => onFirstMonthRuleChange(e.target.value)}
          error={firstMonthRule.length > 0 && !fmValid}
          size="small"
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {!isNew && (
            <Tooltip title="Remove this rule (block transition)">
              <IconButton color="error" onClick={onDelete} disabled={saving}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!allValid || saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function UpgradeMatrix() {
  // Region selection
  const [sourceRegion, setSourceRegion] = useState<string>('DMV');
  const [targetRegion, setTargetRegion] = useState<string>('DMV');

  // Data
  const [sourceTiers, setSourceTiers] = useState<TierRecord[]>([]);
  const [targetTiers, setTargetTiers] = useState<TierRecord[]>([]);
  const [rules, setRules] = useState<UpgradeRule[]>([]);
  const [activePromos, setActivePromos] = useState<Promotion[]>([]);

  // Available regions
  const [regions, setRegions] = useState<string[]>(['DMV']);

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editFrom, setEditFrom] = useState<TierTypeCombo | null>(null);
  const [editTo, setEditTo] = useState<TierTypeCombo | null>(null);
  const [editFee, setEditFee] = useState('X');
  const [editProration, setEditProration] = useState('X');
  const [editFirstMonth, setEditFirstMonth] = useState('X');
  const [editIsNew, setEditIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  // -------------------------------------------------------------------------
  // Load data
  // -------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sTiers, tTiers, allRules, regionList, allPromos] = await Promise.all([
        loadTiersForRegion(sourceRegion),
        sourceRegion === targetRegion
          ? loadTiersForRegion(sourceRegion)
          : loadTiersForRegion(targetRegion),
        loadUpgradeRulesForRegion(sourceRegion),
        loadRegions(),
        getPromotions(),
      ]);
      if (regionList.length > 0) setRegions(regionList);
      setSourceTiers(sTiers);
      setTargetTiers(sourceRegion === targetRegion ? sTiers : tTiers);

      // Filter rules to only those targeting the selected target region
      const filtered = allRules.filter((r) => r.toRegion === targetRegion);
      setRules(filtered);

      // Filter to active promotions only
      const today = new Date().toISOString().split('T')[0];
      setActivePromos(allPromos.filter(p => p.startDate <= today && p.endDate >= today));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
      console.error('UpgradeMatrix: failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [sourceRegion, targetRegion]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const sourceCombos = useMemo(() => buildCombos(sourceTiers), [sourceTiers]);
  const targetCombos = useMemo(() => buildCombos(targetTiers), [targetTiers]);

  /** Map from "fromTierId|fromType|toTierId|toType" to UpgradeRule */
  const ruleMap = useMemo(() => {
    const map = new Map<string, UpgradeRule>();
    for (const r of rules) {
      map.set(ruleKey(r.fromTierId, r.fromType, r.toTierId, r.toType), r);
    }
    return map;
  }, [rules]);

  /** Map from same key to active Promotion (if any) */
  const promoMap = useMemo(() => {
    const map = new Map<string, Promotion>();
    for (const p of activePromos) {
      map.set(ruleKey(p.fromTierId, p.fromType, p.toTierId, p.toType), p);
    }
    return map;
  }, [activePromos]);

  // -------------------------------------------------------------------------
  // Cell click handler
  // -------------------------------------------------------------------------

  const handleCellClick = useCallback(
    (from: TierTypeCombo, to: TierTypeCombo) => {
      const existing = ruleMap.get(
        ruleKey(from.tierId, from.type, to.tierId, to.type),
      );
      setEditFrom(from);
      setEditTo(to);
      if (existing) {
        setEditFee(existing.transactionFee);
        setEditProration(existing.prorationRule);
        setEditFirstMonth(existing.firstMonthRule);
        setEditIsNew(false);
      } else {
        setEditFee('X');
        setEditProration('X');
        setEditFirstMonth('X');
        setEditIsNew(true);
      }
      setDialogOpen(true);
    },
    [ruleMap],
  );

  // -------------------------------------------------------------------------
  // Save handler
  // -------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!editFrom || !editTo) return;
    setSaving(true);
    try {
      const rule: UpgradeRule = {
        fromTierId: editFrom.tierId,
        fromType: editFrom.type,
        fromRegion: sourceRegion,
        toTierId: editTo.tierId,
        toType: editTo.type,
        toRegion: targetRegion,
        transactionFee: editFee.trim(),
        prorationRule: editProration.trim(),
        firstMonthRule: editFirstMonth.trim(),
      };
      await saveRule(rule);
      // Update local state optimistically
      setRules((prev) => {
        const key = ruleKey(
          rule.fromTierId,
          rule.fromType,
          rule.toTierId,
          rule.toType,
        );
        const idx = prev.findIndex(
          (r) =>
            ruleKey(r.fromTierId, r.fromType, r.toTierId, r.toType) === key,
        );
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = rule;
          return next;
        }
        return [...prev, rule];
      });
      setDialogOpen(false);
    } catch (err) {
      console.error('UpgradeMatrix: failed to save rule', err);
      setError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  }, [
    editFrom,
    editTo,
    editFee,
    editProration,
    editFirstMonth,
    sourceRegion,
    targetRegion,
  ]);

  // -------------------------------------------------------------------------
  // Delete handler
  // -------------------------------------------------------------------------

  const handleDelete = useCallback(async () => {
    if (!editFrom || !editTo) return;
    setSaving(true);
    try {
      await deleteRule({
        fromRegion: sourceRegion,
        fromTierId: editFrom.tierId,
        fromType: editFrom.type,
        toRegion: targetRegion,
        toTierId: editTo.tierId,
        toType: editTo.type,
        transactionFee: '',
        prorationRule: '',
        firstMonthRule: '',
      });
      // Remove from local state
      setRules((prev) =>
        prev.filter(
          (r) =>
            ruleKey(r.fromTierId, r.fromType, r.toTierId, r.toType) !==
            ruleKey(
              editFrom.tierId,
              editFrom.type,
              editTo.tierId,
              editTo.type,
            ),
        ),
      );
      setDialogOpen(false);
    } catch (err) {
      console.error('UpgradeMatrix: failed to delete rule', err);
      setError(err instanceof Error ? err.message : 'Failed to delete rule');
    } finally {
      setSaving(false);
    }
  }, [editFrom, editTo, sourceRegion, targetRegion]);

  // -------------------------------------------------------------------------
  // Region change handlers
  // -------------------------------------------------------------------------

  const handleSourceChange = useCallback((e: SelectChangeEvent) => {
    const region = e.target.value;
    setSourceRegion(region);
    setTargetRegion(region);
  }, []);

  const handleTargetChange = useCallback((e: SelectChangeEvent) => {
    setTargetRegion(e.target.value);
  }, []);

  // -------------------------------------------------------------------------
  // Export to CSV
  // -------------------------------------------------------------------------

  const handleExportCsv = useCallback(() => {
    const escCsv = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const header = ['From \\ To', ...targetCombos.map(c => `${c.label} ($${c.price})`)];
    const rows = sourceCombos.map(row => {
      const cells = targetCombos.map(col => {
        const isDiag = sourceRegion === targetRegion && row.tierId === col.tierId && row.type === col.type;
        if (isDiag) return '';
        const key = ruleKey(row.tierId, row.type, col.tierId, col.type);
        const promo = promoMap.get(key);
        const rule = ruleMap.get(key);
        const diff = col.price - row.price;
        const diffStr = diff >= 0 ? `+$${diff.toFixed(2)}` : `-$${Math.abs(diff).toFixed(2)}`;
        if (promo) {
          return `${diffStr} PROMO:${promo.transactionFeeOverride}/${promo.prorationRuleOverride}/${promo.firstMonthRuleOverride}`;
        }
        if (rule) {
          return `${diffStr} ${rule.transactionFee}/${rule.prorationRule}/${rule.firstMonthRule}`;
        }
        return `${diffStr} (blocked)`;
      });
      return [`${row.label} ($${row.price})`, ...cells];
    });
    const csv = [header, ...rows].map(r => r.map(escCsv).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upgrade-matrix-${sourceRegion}-to-${targetRegion}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sourceCombos, targetCombos, ruleMap, promoMap, sourceRegion, targetRegion]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Upgrade Matrix
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each cell shows{' '}
        <strong>transactionFee / prorationRule / firstMonthRule</strong>. Click
        any cell to edit or create a transition rule.
      </Typography>

      {/* Region pickers */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Source Region</InputLabel>
          <Select
            value={sourceRegion}
            label="Source Region"
            onChange={handleSourceChange}
          >
            {regions.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Target Region</InputLabel>
          <Select
            value={targetRegion}
            label="Target Region"
            onChange={handleTargetChange}
          >
            {regions.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCsv}
          disabled={loading || sourceCombos.length === 0}
          size="small"
        >
          Export CSV
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Matrix table */}
      {!loading && sourceCombos.length > 0 && targetCombos.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(100vh - 320px)',
            overflow: 'auto',
            mb: 3,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ minWidth: targetCombos.length * 60 + 200 }}
          >
            <TableHead>
              <TableRow>
                {/* Top-left corner cell */}
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    backgroundColor: '#fafafa',
                    fontWeight: 'bold',
                    minWidth: 180,
                    borderRight: '2px solid #e0e0e0',
                  }}
                >
                  From \ To
                </TableCell>
                {targetCombos.map((col) => (
                  <TableCell
                    key={`${col.tierId}-${col.type}`}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '0.65rem',
                      textAlign: 'left',
                      p: 0.5,
                      minWidth: 50,
                      maxWidth: 60,
                      height: 140,
                      verticalAlign: 'bottom',
                      backgroundColor: '#fafafa',
                      overflow: 'hidden',
                    }}
                  >
                    <Tooltip title={`${col.label} ($${col.price.toFixed(2)})`} arrow>
                      <Box
                        sx={{
                          transform: 'rotate(-55deg)',
                          transformOrigin: 'bottom left',
                          whiteSpace: 'nowrap',
                          ml: 2.5,
                          mb: 0.5,
                        }}
                      >
                        {col.label}
                        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5, fontSize: '0.6rem' }}>
                          ${col.price}
                        </Box>
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sourceCombos.map((row) => (
                <TableRow key={`${row.tierId}-${row.type}`} hover={false}>
                  {/* Sticky first column: row label */}
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: '#fafafa',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      borderRight: '2px solid #e0e0e0',
                      p: 1,
                    }}
                  >
                    {row.label}{' '}
                    <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                      ${row.price}
                    </Box>
                  </TableCell>
                  {targetCombos.map((col) => {
                    const isDiagonal =
                      sourceRegion === targetRegion &&
                      row.tierId === col.tierId &&
                      row.type === col.type;
                    const key = ruleKey(row.tierId, row.type, col.tierId, col.type);
                    const rule = ruleMap.get(key);
                    const promo = promoMap.get(key);
                    return (
                      <MatrixCell
                        key={`${col.tierId}-${col.type}`}
                        rule={rule}
                        promo={promo}
                        isDiagonal={isDiagonal}
                        fromLabel={row.label}
                        toLabel={col.label}
                        fromPrice={row.price}
                        toPrice={col.price}
                        onClick={() => {
                          if (!isDiagonal) {
                            handleCellClick(row, col);
                          }
                        }}
                      />
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Empty state */}
      {!loading &&
        (sourceCombos.length === 0 || targetCombos.length === 0) &&
        !error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No tiers found for the selected region(s). Make sure tier data has
            been loaded into DynamoDB.
          </Alert>
        )}

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Legend
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#e8f5e9',
                border: '1px solid #ccc',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Allowed (default values)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#fff8e1',
                border: '1px solid #ccc',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Allowed (custom values)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#f5f5f5',
                border: '1px solid #ccc',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Blocked (no rule)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#424242',
                border: '1px solid #ccc',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Same plan (diagonal)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#f3e5f5',
                border: '2px solid #9c27b0',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Active promotion override</Typography>
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Cell format:{' '}
          <strong>transactionFee / prorationRule / firstMonthRule</strong>.
          Values: <strong>X</strong>=default, <strong>0</strong>=free,{' '}
          <strong>$amount</strong>=fixed, <strong>%nn</strong>=discount
          percentage. Cells with <strong style={{ color: '#9c27b0' }}>purple border</strong> have an active promotion overriding the base rule.
          {activePromos.length > 0 && (
            <strong> ({activePromos.length} active promotion{activePromos.length !== 1 ? 's' : ''})</strong>
          )}
        </Typography>
      </Paper>

      {/* Edit dialog */}
      <EditDialog
        open={dialogOpen}
        fromLabel={editFrom?.label ?? ''}
        toLabel={editTo?.label ?? ''}
        fromPrice={editFrom?.price ?? 0}
        toPrice={editTo?.price ?? 0}
        transactionFee={editFee}
        prorationRule={editProration}
        firstMonthRule={editFirstMonth}
        isNew={editIsNew}
        saving={saving}
        onTransactionFeeChange={setEditFee}
        onProrationRuleChange={setEditProration}
        onFirstMonthRuleChange={setEditFirstMonth}
        onSave={() => void handleSave()}
        onDelete={() => void handleDelete()}
        onCancel={() => setDialogOpen(false)}
      />
    </Box>
  );
}
